import { useState, type ReactNode } from 'react';
import { Button, Card, CardTitle, EmptyState, useNow } from '@wundo/ui';
import { ClipboardList, Sparkles } from 'lucide-react';
import type {
  MeetingPageResponse,
  MeetingReportSection,
} from '@wundo/shared/meetingPage.ts';
import { formatDayAndMonth } from '@/components/agenda/meetingFormat.ts';
import { MeetingActionItems } from './MeetingActionItems.tsx';
import { MeetingMaterialList } from './MeetingMaterialList.tsx';
import { MeetingRegenerateRow } from './MeetingRegenerateRow.tsx';
import { MeetingReportSectionCard } from './MeetingReportSectionCard.tsx';
import { MeetingCopyButton } from './MeetingCopyButton.tsx';
import { MeetingShareNotesDialog } from './MeetingShareNotesDialog.tsx';
import type { MeetingActionItemHandlers } from './meetingActionItemHandlers.ts';
import { meetingReportState, type MeetingReportState } from './meetingReportState.ts';
import { useAutosavedDocument } from './useAutosavedDocument.ts';
import type { DocumentSaveOptions } from '@/lib/meetingPage.ts';
import type { RichTextEditorWiring } from '@/lib/richTextEditor.ts';
import { m } from '@/paraglide/messages.js';

/**
 * What a verslag that is on its way is coming from: the user's own recording,
 * the notetaker's, or a writer already running.
 */
export type MeetingReportWaiting = 'microphone' | 'notetaker' | 'writing';

export interface MeetingReportPanelProps {
  page: MeetingPageResponse;
  waiting: MeetingReportWaiting | null;
  /* The user's own recording could not be read back. */
  recordingFailed: boolean;
  generating: boolean;
  generateFailed: boolean;
  copied: boolean;
  editor: RichTextEditorWiring;
  actionItems: MeetingActionItemHandlers;
  onGenerate: () => void;
  /** One gesture for the shared notes: keep them, and write from them. */
  onPasteGenerate: (content: string) => void;
  /** The note's full text, for the dialog to open with. */
  onLoadNote: () => Promise<string | null>;
  onRegenerate: () => void;
  onSave: (sections: MeetingReportSection[], options: DocumentSaveOptions) => void;
  onDeleteMaterial: (materialId: string) => void;
  onCopy: () => void;
}

/**
 * The Verslag tab: what happened, and what was agreed. It shows one thing at a
 * time: the wait for a verslag that writes itself, the invitation to share
 * notes where Wundo was not in the room, or the verslag itself. Opening the tab
 * never writes one; that is always an explicit ask or a transcript arriving.
 */
export function MeetingReportPanel({
  page,
  waiting,
  recordingFailed,
  generating,
  generateFailed,
  copied,
  editor,
  actionItems,
  onGenerate,
  onPasteGenerate,
  onLoadNote,
  onRegenerate,
  onSave,
  onDeleteMaterial,
  onCopy,
}: MeetingReportPanelProps) {
  const [sections, write] = useAutosavedDocument(page.report?.sections ?? null, onSave);
  /*
   * The share-notes dialog and what it opens with: null while closed, the
   * note's text (or '' when there is none) while open.
   */
  const [sharing, setSharing] = useState<string | null>(null);

  /*
   * A load that fails opens nothing: an empty field would invite saving blankness over
   * the note.
   */
  const openShare = () => {
    void onLoadNote()
      .then((content) => setSharing(content ?? ''))
      .catch(() => setSharing(null));
  };

  /* Only reachable before a verslag exists, so it always writes one. */
  const submitNotes = (content: string) => {
    setSharing(null);
    onPasteGenerate(content);
  };

  return (
    <div className='mt-6 flex flex-col gap-6'>
      {/* One card, as the prep is: the verslag is a document, not a stack of blocks. */}
      <Card as='section' padding='document'>
        <div className='flex items-center justify-between gap-3'>
          <CardTitle>{m.meeting_tab_report()}</CardTitle>
          {sections && <MeetingCopyButton copied={copied} onCopy={onCopy} />}
        </div>

        {sections ? (
          <ReportDocument
            page={page}
            sections={sections}
            generating={generating}
            generateFailed={generateFailed}
            editor={editor}
            actionItems={actionItems}
            onChange={write}
            onRegenerate={onRegenerate}
          />
        ) : (
          <ReportInvite
            page={page}
            waiting={waiting}
            recordingFailed={recordingFailed}
            generating={generating}
            generateFailed={generateFailed}
            onGenerate={onGenerate}
            onDeleteMaterial={onDeleteMaterial}
            onShare={openShare}
          />
        )}
      </Card>

      {/* Mounted per opening, so it always starts from what was loaded. */}
      {sharing !== null && (
        <MeetingShareNotesDialog
          open
          pending={generating}
          initial={sharing === '' ? null : sharing}
          submitLabel={m.meeting_report_generate()}
          onSubmit={submitNotes}
          onCancel={() => setSharing(null)}
        />
      )}
    </div>
  );
}

type ReportInviteProps = Pick<
  MeetingReportPanelProps,
  | 'page'
  | 'waiting'
  | 'recordingFailed'
  | 'generating'
  | 'generateFailed'
  | 'onGenerate'
  | 'onDeleteMaterial'
> & { onShare: () => void };

/* No verslag yet: what it waits on, the way to share notes, the material so far. */
function ReportInvite({
  page,
  waiting,
  recordingFailed,
  generating,
  generateFailed,
  onGenerate,
  onDeleteMaterial,
  onShare,
}: ReportInviteProps) {
  const now = useNow();
  const state = meetingReportState(page, now);
  const busy = waiting !== null;

  return (
    <>
      <EmptyState
        headingLevel='h3'
        title={inviteTitle(waiting, recordingFailed)}
        busy={busy}
        description={waitingDescription(waiting, recordingFailed, state)}
        action={shareInvite(waiting, state, onShare)}
      />

      {/* The retry after a failed write; not while Wundo is already writing from it. */}
      {!busy && page.materials.length > 0 && (
        <div className='flex flex-col gap-3 border-t border-fill pt-4'>
          <MeetingMaterialList materials={page.materials} onDelete={onDeleteMaterial} />
          <div className='flex flex-col items-start gap-2'>
            {generateFailed && (
              <p role='alert' className='text-sm text-danger'>
                {m.meeting_report_generate_failed()}
              </p>
            )}
            {/* Nothing to write from is not a disabled button, it is no button. */}
            {page.report_available && (
              <Button size='sm' disabled={generating} onClick={onGenerate}>
                <Sparkles className='size-4' aria-hidden='true' />
                {generateFailed
                  ? m.meeting_report_generate_retry()
                  : m.meeting_report_generate()}
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

type ReportDocumentProps = Pick<
  MeetingReportPanelProps,
  'page' | 'generating' | 'generateFailed' | 'editor' | 'actionItems' | 'onRegenerate'
> & {
  sections: MeetingReportSection[];
  onChange: (sections: MeetingReportSection[]) => void;
};

/* The verslag itself, with where it came from and the agreements made in this meeting. */
function ReportDocument({
  page,
  sections,
  generating,
  generateFailed,
  editor,
  actionItems,
  onChange,
  onRegenerate,
}: ReportDocumentProps) {
  const changeSection = (title: string, text: string) =>
    onChange(
      sections.map((section) =>
        section.title === title ? { ...section, text } : section,
      ),
    );

  /* Only this meeting's agreements: the preparation already lists the whole thread's. */
  const own = page.action_items.filter((item) => item.meeting_id === page.meeting.id);
  const sources = reportSources(page);

  return (
    <>
      {/* Where the text came from, said once. The transcript itself is never shown. */}
      {page.report && (
        <p className='mt-2 text-xs text-ink-muted italic'>
          {m.meeting_report_provenance({
            date: formatDayAndMonth(new Date(page.report.generated_at)),
          })}
        </p>
      )}

      <div className='mt-4 flex flex-col gap-4'>
        {sections.map((section) => (
          <MeetingReportSectionCard
            key={section.title}
            section={section}
            editor={editor}
            onChange={(text) => changeSection(section.title, text)}
          />
        ))}
      </div>

      {/* Once the verslag exists, the material behind it is one quiet line. */}
      <div className='mt-4 flex flex-wrap items-center gap-x-2 gap-y-1'>
        {sources.length > 0 && (
          <span className='text-xs text-ink-muted'>
            {m.meeting_report_sources({ sources: sources.join(' · ') })}
          </span>
        )}
      </div>

      <div className='mt-5 border-t border-fill pt-5'>
        <MeetingActionItems
          items={own}
          title={m.meeting_report_actions_title()}
          emptyLabel={m.meeting_report_actions_empty()}
          threadId={page.thread?.id ?? null}
          doneCount={page.action_items_done_count}
          {...actionItems}
        />
      </div>

      <MeetingRegenerateRow
        available={page.report_available}
        generating={generating}
        failed={generateFailed}
        labels={{
          regenerate: m.meeting_report_regenerate(),
          generating: m.meeting_report_generating(),
          failed: m.meeting_report_generate_failed(),
        }}
        onRegenerate={onRegenerate}
      />
    </>
  );
}

/* Which sources the verslag was written from, in the order they are read. */
function reportSources(page: MeetingPageResponse): string[] {
  const transcribed = page.materials.some((material) => material.kind === 'transcript');
  const pasted = page.materials.some((material) => material.kind === 'pasted');
  return [
    transcribed ? m.meeting_report_sources_transcript() : null,
    pasted ? m.meeting_report_sources_notes() : null,
  ].filter((source) => source !== null);
}

function inviteTitle(
  waiting: MeetingReportWaiting | null,
  recordingFailed: boolean,
): string {
  if (waiting === 'microphone' || waiting === 'notetaker') {
    return m.meeting_report_pending_title();
  }
  if (recordingFailed) return m.meeting_offline_failed_title();
  return m.meeting_report_invite_title();
}

function waitingDescription(
  waiting: MeetingReportWaiting | null,
  recordingFailed: boolean,
  state: MeetingReportState,
): ReactNode {
  switch (waiting) {
    case 'microphone':
      return (
        <>
          {m.meeting_offline_processing_body()}
          <span className='mt-2 block text-sm'>
            {m.meeting_offline_processing_note()}
          </span>
        </>
      );
    case 'notetaker':
      return m.meeting_report_notetaker_body();
    case 'writing':
      return m.meeting_report_generating();
    case null:
      return invitationBody(recordingFailed, state);
  }
}

/* A recording that could not be read back would otherwise go on promising one. */
function invitationBody(recordingFailed: boolean, state: MeetingReportState): string {
  if (recordingFailed) return m.meeting_offline_failed_body();
  if (state === 'paste') return m.meeting_report_invite_body();
  return m.meeting_report_auto();
}

/*
 * The way in, matched to the promise: the tab's whole invitation where nothing
 * is coming on its own, the quiet way past the notetaker's wait, and nothing
 * while the material is already in and being written up.
 */
function shareInvite(
  waiting: MeetingReportWaiting | null,
  state: MeetingReportState,
  openShare: () => void,
): ReactNode {
  if (waiting === 'notetaker') {
    return (
      <Button size='sm' variant='quiet' onClick={openShare}>
        {m.meeting_report_paste_now()}
      </Button>
    );
  }
  if (waiting === null && state === 'paste') {
    return (
      <Button size='sm' onClick={openShare}>
        <ClipboardList className='size-4' aria-hidden='true' />
        {m.meeting_report_share_notes()}
      </Button>
    );
  }
  return undefined;
}
